/**
 *
 * UploadCard
 *
 */
import * as React from 'react';

import { Box, Card, IconButton, Stack, Typography } from '@mui/joy';

import toast from 'react-hot-toast';
import {
  DeleteRounded,
  HighlightOff,
  CloudUpload,
  CheckCircle,
  UploadFile,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

interface UploadCardProps {
  title: string;
  description: string;
  file: File | FileList | undefined;
  onRemove: (index?: number) => void;
  MAX_FILE_SIZE: number;
  setFile: (file: File | FileList | undefined) => void;
  multiple?: boolean;
  disabled?: boolean;
  slot?: {
    alert?: React.ReactNode
  }
}

export const UploadCard = ({
  title,
  description,
  file,
  onRemove,
  MAX_FILE_SIZE,
  setFile,
  multiple = false,
  slot,
  disabled,
}: UploadCardProps) => {
  const hasFile =
    !!file && (multiple ? file instanceof FileList && file.length > 0 : true);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      if (!multiple) {
        if (acceptedFiles.length > 1) {
          toast.error('Please upload only one file');
          return;
        }

        const file = acceptedFiles[0];
        if (file.size > MAX_FILE_SIZE) {
          toast.error(
            `File size should not exceed ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
          );
          return;
        }
        setFile(file);
      } else {
        const oversizedFiles = acceptedFiles.filter(
          file => file.size > MAX_FILE_SIZE,
        );
        if (oversizedFiles.length > 0) {
          toast.error(
            `File size should not exceed ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
          );
          return;
        }
        const dataTransfer = new DataTransfer();
        acceptedFiles.forEach(file => dataTransfer.items.add(file));
        setFile(dataTransfer.files);
      }
    },
    [multiple, MAX_FILE_SIZE, setFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/zip': ['.zip'],
      'application/x-zip-compressed': ['.zip'],
    },
    disabled: disabled,
    multiple: multiple,
    noClick: false,
    noKeyboard: false,
    onDragEnter: () => { },
    onDragLeave: () => { },
    onDragOver: () => { },
    validator: file => {
      if (file.size > MAX_FILE_SIZE) {
        return {
          code: 'file-too-large',
          message: `File size should not exceed ${MAX_FILE_SIZE / (1024 * 1024)
            }MB`,
        };
      }
      return null;
    },
  });

  const { ref, ...dropzoneProps } = getRootProps();
  return (
    <div {...dropzoneProps}>
      <Card
        variant="outlined"
        sx={{
          p: 3,
          borderStyle: 'solid',
          borderColor: theme => (isDragActive ? 'primary.main' : 'divider'),
          borderWidth: isDragActive ? '2px' : '1px',
          cursor: disabled ? 'context-menu' : 'pointer',
          transition: 'all 0.2s',
          minHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          transform: isDragActive ? 'scale(1.02)' : 'scale(1)',
          ...(
            disabled
              ?
              {
                opacity: 0.5,
                backgroundColor: '#ddd'
              }
              :
              {
                backgroundColor: 'background.surface',
                '&:hover': {
                  backgroundColor: isDragActive ? 'primary.softBg' : 'action.hover',
                  borderColor: isDragActive ? 'primary.main' : 'primary.softBg',
                  '& .upload-icon': {
                    transform: 'scale(1.1)',
                  },
                },
              }
          )
        }}
      >
        <input {...getInputProps()} />
        <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
          {!hasFile ? (
            <>
              {
                slot?.alert
              }
              <CloudUpload
                className="upload-icon"
                sx={{
                  fontSize: '48px',
                  color: 'primary.main',
                  transition: 'transform 0.2s',
                }}
              />
              <div>
                <Typography level="title-lg" sx={{ mb: 0.5 }}>
                  {title}
                </Typography>

                <Typography level="body-sm">{description}</Typography>
                <Typography level="body-sm" sx={{ mt: 1, color: 'neutral.500' }}>
                  PDF or ZIP files, {MAX_FILE_SIZE / (1024 * 1024)}MB max
                </Typography>
              </div>
            </>
          ) : (
            <>
              <CheckCircle
                sx={{
                  fontSize: '48px',
                  color: 'green',
                }}
              />
              {file instanceof File && (
                <Box sx={{ width: '100%' }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 'sm',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <UploadFile />
                      <Typography>{file.name}</Typography>
                    </Stack>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      onClick={e => {
                        e.stopPropagation();
                        onRemove();
                      }}
                    >
                      <HighlightOff />
                    </IconButton>
                  </Stack>
                </Box>
              )}
              {file instanceof FileList && (
                <Box sx={{ width: '100%' }}>
                  <Stack spacing={1}>
                    {Array.from(file).map((f, index) => (
                      <Stack
                        key={index}
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 'sm',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center' }}
                        >
                          <UploadFile />
                          <Typography>{f.name}</Typography>
                        </Stack>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={e => {
                            e.stopPropagation();
                            onRemove(index);
                          }}
                        >
                          <DeleteRounded />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Card>
    </div>
  );
};
